import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import type { ApiResponse } from '@/types/api';
import type { AdminUser } from '@/types/auth';
import { API_BASE_URL, LANG_STORAGE_KEY, DEFAULT_LANGUAGE } from '@/utils/constants';
import { handleApiError } from '@/utils/apiError';
import { getAuthArea, getLoginPath, type AuthArea } from '@/utils/authArea';

/** Endpoints whose errors are handled by the caller (no global toast). */
const SILENT_ERROR_PATHS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/logout',
  '/admin/auth/login',
  '/admin/auth/refresh',
  '/admin/auth/logout',
];

/** Endpoints whose 401 must not trigger a silent refresh. */
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/admin/auth/login',
  '/admin/auth/refresh',
  '/admin/auth/logout',
];

/**
 * Bound the refresh wait so a hung/unreachable backend can't trap the app on
 * the bootstrap spinner forever. On timeout we fall through to the login page.
 */
const REFRESH_TIMEOUT_MS = 15000;

type AuthRequestConfig = InternalAxiosRequestConfig & {
  /** Session the request was sent with; a 401 refreshes this session. */
  _authArea?: AuthArea;
  _retry?: boolean;
};

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // carry the httpOnly refresh-token cookie
});

// ── Request interceptor: inject Access Token + Accept-Language ──────

apiClient.interceptors.request.use((config: AuthRequestConfig) => {
  // Choose the token by the area being viewed, not by the endpoint: admin
  // pages also call admin-only endpoints outside /admin/ (DELETE /files/{id}),
  // and the customer site never calls /admin/ endpoints.
  const area = getAuthArea(window.location.pathname);
  config._authArea = area;
  const { accessToken, adminAccessToken } = useAuthStore.getState();
  const token = area === 'admin' ? adminAccessToken : accessToken;
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const lang = localStorage.getItem(LANG_STORAGE_KEY) || DEFAULT_LANGUAGE;
  config.headers['Accept-Language'] = lang;
  // The instance default Content-Type is JSON, but axios only sends FormData
  // as a real multipart body when it doesn't already see a JSON content type
  // on the request — otherwise it silently JSON.stringifies the FormData
  // (collapsing any File entries to `{}`). Drop it here so the browser sets
  // the correct `multipart/form-data; boundary=...` header itself.
  if (config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

// ── Response interceptor: 401 auto token refresh with queue ─────────

type QueuedRequest = {
  resolve: (token: string) => void;
  reject: (reason?: unknown) => void;
};

/** One in-flight refresh, and the requests waiting on it, per session. */
const refreshState: Record<AuthArea, { isRefreshing: boolean; failedQueue: QueuedRequest[] }> = {
  customer: { isRefreshing: false, failedQueue: [] },
  admin: { isRefreshing: false, failedQueue: [] },
};

const processQueue = (area: AuthArea, error: unknown | null, token = '') => {
  const state = refreshState[area];
  state.failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  state.failedQueue = [];
};

/**
 * Restore the customer session from its httpOnly refresh cookie
 * (POST /auth/refresh). Returns the new access token, or throws on failure.
 *
 * Exported so the app bootstrap and the OAuth callback page can restore the
 * customer session on page load.
 */
export async function refreshAccessToken(): Promise<string> {
  const { data } = await axios.post<ApiResponse<{ access_token: string }>>(
    `${API_BASE_URL}/auth/refresh`,
    {},
    { withCredentials: true, timeout: REFRESH_TIMEOUT_MS },
  );
  const token = data.data.access_token;
  useAuthStore.getState().setAccessToken(token);
  return token;
}

/**
 * Restore the admin session from the admin httpOnly refresh cookie
 * (POST /admin/auth/refresh). The response carries the admin payload, so
 * permissions are restored along with the token. Throws on failure.
 */
export async function refreshAdminAccessToken(): Promise<string> {
  const { data } = await axios.post<
    ApiResponse<{ access_token: string; admin: AdminUser }>
  >(
    `${API_BASE_URL}/admin/auth/refresh`,
    {},
    { withCredentials: true, timeout: REFRESH_TIMEOUT_MS },
  );
  const { access_token: token, admin } = data.data;
  useAuthStore.getState().setAdminAuth(token, admin);
  return token;
}

const refreshSession = (area: AuthArea) =>
  area === 'admin' ? refreshAdminAccessToken() : refreshAccessToken();

/** Clear only the expired session and send the user to its login page. */
const endSession = (area: AuthArea) => {
  const store = useAuthStore.getState();
  if (area === 'admin') {
    store.clearAdminAuth();
  } else {
    store.logout();
  }
  window.location.href = getLoginPath(area);
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AuthRequestConfig;

    const url = originalRequest.url ?? '';
    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => url.includes(p));

    // On 401, try a single silent refresh of the session the request was
    // sent with (customer or admin), using that session's httpOnly cookie.
    // If it fails, only that session is cleared (req 19.6).
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // Mark before branching so a request that rides in on someone else's
      // in-flight refresh (the queued branch below) still only ever gets
      // one retry — otherwise a retry that 401s again for an unrelated
      // reason (e.g. a token that can never satisfy this endpoint) would
      // re-enter this branch and trigger another refresh, forever.
      originalRequest._retry = true;

      const area = originalRequest._authArea ?? getAuthArea(window.location.pathname);
      const state = refreshState[area];

      // The request interceptor only fills in Authorization when the header
      // is absent, and the replayed config still carries the bearer from the
      // first attempt. Overwrite it with the refreshed token, or the replay
      // re-sends the token that just 401'd and _retry blocks any further
      // refresh — the refresh succeeds and fixes nothing.
      const replayWithToken = (token: string) => {
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return apiClient(originalRequest);
      };

      if (state.isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          state.failedQueue.push({ resolve, reject });
        }).then(replayWithToken);
      }

      state.isRefreshing = true;

      try {
        const token = await refreshSession(area);
        processQueue(area, null, token);
        return replayWithToken(token);
      } catch (refreshError) {
        processQueue(area, refreshError);
        endSession(area);
        return Promise.reject(refreshError);
      } finally {
        state.isRefreshing = false;
      }
    }

    // Global error toast for all other errors (req 3.5).
    const status = error.response?.status;
    const isSilentPath = SILENT_ERROR_PATHS.some((p) => url.includes(p));
    if (!isSilentPath && status !== 401) {
      handleApiError(error as AxiosError<ApiResponse>);
    }

    return Promise.reject(error);
  },
);

export default apiClient;
